import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, CheckCircle2, ArrowLeft, Search, Users } from "lucide-react";
import api, { errMsg } from "../lib/api";
import { formatRupiah } from "../lib/format";
import { AmountInput, QuickAmounts } from "../components/AmountInput";
import { PinDialog } from "../components/PinDialog";

export default function TransferUser() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [recipient, setRecipient] = useState(null);
  const [searching, setSearching] = useState(false);
  const [amount, setAmount] = useState(0);
  const [note, setNote] = useState("");
  const [wallet, setWallet] = useState(null);
  const [pinOpen, setPinOpen] = useState(false);
  const [idemKey] = useState(() => crypto.randomUUID());

  useEffect(() => {
    api.get("/wallet").then((r) => setWallet(r.data));
  }, []);

  const search = async () => {
    if (!email) return;
    setSearching(true);
    setRecipient(null);
    try {
      const res = await api.get(`/users/lookup?email=${encodeURIComponent(email)}`);
      if (res.data.is_self) {
        toast.error("Tidak bisa transfer ke akun sendiri");
        return;
      }
      setRecipient(res.data);
    } catch (err) {
      toast.error(errMsg(err, "Pengguna tidak ditemukan"));
    } finally {
      setSearching(false);
    }
  };

  const openPin = () => {
    if (!recipient) {
      toast.error("Cari penerima terlebih dahulu");
      return;
    }
    if (amount < 1000) {
      toast.error("Minimal transfer Rp 1.000");
      return;
    }
    if (wallet && amount > wallet.balance) {
      toast.error("Saldo tidak cukup");
      return;
    }
    setPinOpen(true);
  };

  const confirmWithPin = async (pin) => {
    const res = await api.post("/transfers/wallet", {
      recipient_email: recipient.email,
      amount,
      note,
      pin,
      idempotency_key: idemKey,
    });
    setPinOpen(false);
    toast.success("Transfer berhasil!");
    navigate(`/transaksi/${res.data.id}`);
  };

  return (
    <div className="max-w-xl mx-auto space-y-5">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate("/transfer")} aria-label="Kembali" data-testid="transfer-user-back-button">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="font-heading text-xl font-semibold">Transfer ke Sesama Pengguna</h1>
          <p className="text-xs text-muted-foreground">Instan & gratis</p>
        </div>
      </div>

      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
        <Card className="rounded-2xl">
          <CardContent className="p-5 space-y-4">
            <div className="space-y-1.5">
              <Label>Email Penerima</Label>
              <div className="flex gap-2">
                <Input
                  data-testid="transfer-user-email-input"
                  className="h-11"
                  type="email"
                  placeholder="nama@email.com"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setRecipient(null); }}
                  onKeyDown={(e) => e.key === "Enter" && search()}
                />
                <Button variant="outline" className="h-11 border-primary text-primary hover:bg-primary/5" onClick={search} disabled={searching} data-testid="transfer-user-search-button">
                  {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                </Button>
              </div>
            </div>
            {recipient && (
              <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 flex items-center gap-3" data-testid="recipient-result-card">
                <CheckCircle2 className="h-6 w-6 text-emerald-600 shrink-0" />
                <div>
                  <p className="font-semibold text-sm" data-testid="recipient-name">{recipient.name}</p>
                  <p className="text-xs text-muted-foreground">{recipient.email}</p>
                </div>
              </motion.div>
            )}
            <div className="space-y-1.5">
              <Label>Nominal</Label>
              <AmountInput value={amount} onChange={setAmount} testId="transfer-user-amount-input" />
              <p className="text-xs text-muted-foreground">
                Saldo kamu: <span className="font-semibold tabular-nums">{wallet ? formatRupiah(wallet.balance) : "..."}</span> · Minimal Rp 1.000
              </p>
            </div>
            <QuickAmounts onPick={setAmount} amounts={[10000, 25000, 50000, 100000]} />
            <div className="space-y-1.5">
              <Label>Catatan (opsional)</Label>
              <Input data-testid="transfer-user-note-input" className="h-11" placeholder="Contoh: patungan makan" value={note} onChange={(e) => setNote(e.target.value)} maxLength={60} />
            </div>
            <Button className="w-full h-12 text-base bg-gradient-to-r from-[#2E7CF6] to-[#1FC8A9] hover:opacity-90" onClick={openPin} disabled={!recipient || amount < 1000} data-testid="transfer-user-submit-button">
              Kirim Sekarang
            </Button>
          </CardContent>
        </Card>
      </motion.div>

      <PinDialog open={pinOpen} onOpenChange={setPinOpen} onConfirm={confirmWithPin} />
    </div>
  );
}
